import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { auth as authApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lightning, TelegramLogo, ChartLineUp, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function LoginPage() {
  const { user, loginDev, loginGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [showDev, setShowDev] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Backend gates dev-login behind ALLOW_DEV_LOGIN + ENVIRONMENT — mirror that
  // on the UI so production never even shows the toggle. Defaults to null
  // (unknown) until the /auth/config call returns; the toggle is hidden
  // during that brief window.
  const [devLoginEnabled, setDevLoginEnabled] = useState(null);
  const googleButtonRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    authApi.config()
      .then((r) => { if (!cancelled) setDevLoginEnabled(!!r.data?.dev_login_enabled); })
      .catch(() => { if (!cancelled) setDevLoginEnabled(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleButtonRef.current) return;
    const handleCredential = (response) => {
      loginGoogle(response.credential)
        .then(() => toast.success("Signed in"))
        .catch((err) => {
          const msg = err?.response?.data?.detail || err?.message || "Google login failed";
          toast.error(msg);
        });
    };
    let cancelled = false;
    const init = () => {
      if (cancelled || !window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: handleCredential });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: "outline", size: "large", width: 320, text: "continue_with",
      });
    };
    if (window.google?.accounts?.id) init();
    else {
      const t = setInterval(() => {
        if (window.google?.accounts?.id) { clearInterval(t); init(); }
      }, 100);
      return () => { cancelled = true; clearInterval(t); };
    }
  }, [loginGoogle]);

  if (user) return <Navigate to="/" replace />;

  const handleDev = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await loginDev(email || "test@example.com", name || "Test User");
      toast.success("Signed in");
    } catch (err) {
      toast.error("Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 grid-bg" data-testid="login-page">
      {/* Left / Hero */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 border-r border-slate-200 overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#4F46E5]/10 border border-[#4F46E5]/25 flex items-center justify-center">
              <Lightning size={22} weight="fill" color="#4F46E5" />
            </div>
            <span className="font-display text-2xl text-slate-900">SIGNAL/DESK</span>
          </div>
          <div className="mt-24">
            <p className="overline text-[10px]">Telegram ⟶ WEEX Automated Executor</p>
            <h1 className="font-display text-5xl xl:text-6xl leading-[1.02] mt-4 text-slate-900">
              Ship every<br />
              signal. Never miss<br />
              a stop loss.
            </h1>
            <p className="text-slate-600 mt-6 max-w-md text-base leading-relaxed">
              A control room for Telegram-driven crypto trading. Parse, protect, execute — even when the provider
              forgot to post the SL.
            </p>
          </div>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-4">
          <Feature icon={<TelegramLogo size={18} color="#0891B2" weight="fill" />} label="Telethon listener" />
          <Feature icon={<ChartLineUp size={18} color="#059669" weight="bold" />} label="WEEX Futures" />
          <Feature icon={<ShieldCheck size={18} color="#D97706" weight="fill" />} label="Fallback SL guard" />
        </div>

        <div
          className="absolute -right-40 -top-40 w-[520px] h-[520px] rounded-full opacity-40 blur-3xl"
          style={{ background: "radial-gradient(circle at center, #4F46E522, transparent 60%)" }}
        />
      </div>

      {/* Right / Form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <p className="overline">STEP 01 · Authentication</p>
          <h2 className="font-display text-3xl mt-2 text-slate-900">Enter the terminal</h2>
          <p className="text-slate-600 mt-2 text-sm">
            Sign in with Google to open your dashboard.
          </p>

          <div className="mt-8" data-testid="google-login-btn">
            {GOOGLE_CLIENT_ID ? (
              <div ref={googleButtonRef} />
            ) : (
              <p className="text-xs text-amber-600 border border-amber-200 bg-amber-50 rounded-md p-3">
                Google sign-in isn't configured on this deployment yet.
              </p>
            )}
          </div>

          {devLoginEnabled && (
            <>
              <div className="flex items-center gap-3 my-6 text-slate-400 text-xs">
                <div className="h-px flex-1 bg-slate-200" />
                <span>or</span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>

              <button
                type="button"
                onClick={() => setShowDev((s) => !s)}
                data-testid="dev-login-toggle"
                className="text-xs text-slate-500 hover:text-slate-900 transition-colors underline underline-offset-4"
              >
                {showDev ? "Hide dev login" : "Use dev / test login (no OAuth)"}
              </button>

              {showDev && (
                <form onSubmit={handleDev} className="mt-4 space-y-3" data-testid="dev-login-form">
                  <Input
                    data-testid="dev-login-email"
                    type="email"
                    placeholder="test@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-white border-slate-200 h-11"
                  />
                  <Input
                    data-testid="dev-login-name"
                    placeholder="Test User"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-white border-slate-200 h-11"
                  />
                  <Button
                    data-testid="dev-login-submit"
                    type="submit"
                    disabled={submitting}
                    className="w-full h-11 bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                  >
                    {submitting ? "Signing in…" : "Enter dashboard"}
                  </Button>
                </form>
              )}
            </>
          )}

          <p className="text-[11px] text-slate-500 mt-10">
            Your credentials never leave this browser. WEEX keys are encrypted-at-rest and never returned to the
            frontend once stored.
          </p>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon, label }) {
  return (
    <div className="card-surface p-3 flex items-center gap-2">
      {icon}
      <span className="text-xs text-slate-600">{label}</span>
    </div>
  );
}
